import { SiteFooterProps } from 'lib/components/SiteFooter/SiteFooter'

import { SiteChromeContent } from '@/components/shipplay/siteChromeContent'
import { BrandEmphasis } from '@/components/shipplay/ui/BrandEmphasis'

export interface Content {
  defaultHelpCenterItems: {
    id: string
    title: string
    content: React.ReactNode
  }[]
  shipPageHelpCenterItems: {
    id: string
    title: string
    content: React.ReactNode
  }[]
  packageAndProtectionHelpCenterItems: {
    id: string
    title: string
    content: React.ReactNode
  }[]
  signedInUserLinks: (activeShipments: number) => { name: string; href: string; target?: string }[]
  siteFooterMeta: SiteFooterProps
  siteChrome: SiteChromeContent
}

const signedInUserLinks = (
  activeShipments: number,
): { name: string; href: string; target?: string }[] => [
  {
    name: 'My Account',
    href: '/users/edit',
  },
  {
    name: `Shipments (${activeShipments})`,
    href: '/users/edit#view-orders',
  },
  {
    name: 'Address Book',
    href: '/users/edit#view-address-book',
  },
  {
    name: 'Gift Cards',
    href: '/gift-cards',
  },
  {
    name: 'Online Bill Pay',
    href: '/invoices/pay',
    target: '_blank',
  },
]

const siteFooterMeta: SiteFooterProps = {
  brand: 'shipsticks',
  showAmpersand: false,
  socialLinks: [
    {
      icon: 'linkedin',
      url: 'https://www.linkedin.com/company/ship-sticks-llc',
    },
    {
      icon: 'facebook',
      url: 'https://www.facebook.com/ShipSticks',
    },
    {
      icon: 'youtube',
      url: 'https://www.youtube.com/channel/UClSOWj5Vc6ICxhGTdPVsQcQ',
    },
    {
      icon: 'instagram',
      url: 'https://www.instagram.com/shipsticks/',
    },
    {
      icon: 'twitter',
      url: 'https://www.twitter.com/ShipSticks',
    },
    {
      icon: 'tiktok',
      url: 'https://www.tiktok.com/@shipsticks',
    },
  ],
  primaryLinks: [
    {
      title: 'ShipSticks',
      children: [
        {
          title: 'How It Works',
          url: '/how-it-works',
        },
        {
          title: 'Guarantee',
          url: '/guarantee',
        },
        {
          title: 'Packing Tips',
          url: '/pack',
        },
        {
          title: 'Order Tracking',
          url: '/track-orders',
        },
        {
          title: 'About Us',
          url: '/about',
        },
        {
          title: 'Careers',
          url: '/careers',
        },
      ],
    },
    {
      title: 'Resources',
      children: [
        {
          title: 'Become a Partner',
          url: '/partnership-benefits',
        },
        {
          title: 'Become an Affiliate',
          url: '/affiliate',
        },
        {
          title: 'Online Bill Pay',
          url: '/invoices/pay',
          target: '_blank',
        },
        {
          title: 'Blog',
          url: '/blog',
          target: '_blank',
        },
        {
          title: 'Reviews',
          url: '/reviews',
        },
      ],
    },
    {
      title: 'Account',
      children: [
        {
          title: 'Gift Cards',
          url: '/gift-cards',
        },
      ],
    },
    {
      title: 'Help',
      children: [
        {
          title: 'Drop-off Locator',
          url: '/drop-off-locator',
        },
        {
          title: 'FAQ',
          url: '/faq',
        },
        {
          title: 'Support',
          url: 'https://support.shipsticks.com/hc/en-us',
          target: '_blank',
        },
        {
          title: 'Claims',
          url: '/claims',
        },
        {
          title: 'Contact Us',
          url: '/contact',
        },
      ],
    },
    {
      title: 'Contact',
      children: [
        {
          title: (
            <>
              11400 N. Jog Road, Ste 300
              <br />
              Palm Beach Gardens
              <br />
              Florida 33418
            </>
          ),
          url: `https://maps.google.com/?q=11400 Jog Rd Suite 300, Palm Beach Gardens, FL 33418`,
        },
        {
          title: '(561) 429-3900',
          url: 'tel:5614293900',
        },
        {
          title: 'info@shipsticks.com',
          url: 'mailto:info@shipsticks.com',
        },
      ],
    },
  ],
  secondaryLinks: [
    { title: 'Terms of Use', url: '/terms' },
    { title: 'Privacy Policy', url: '/privacy' },
    { title: 'Partner Agreement Terms', url: '/partner-agreement-terms' },
    { title: 'Site Map', url: '/sitemap' },
    { title: 'Version 2.0', url: '#' },
  ],
}

const defaultHelpCenterItems = [
  {
    id: 'how-do-i-attach-shipping-labels',
    title: 'How do I attach the shipping labels?',
    content: (
      <>
        <p>
          If you&apos;re using a hard travel case or box, attach your label with clear packing tape,
          making sure the barcode is visible and secure. If your bag doesn&apos;t have a hard
          surface, you&apos;ll need to place the label inside a luggage tag, which you can pick up
          from your local FedEx, UPS, or DHL location, and attach it to the handle. Don&apos;t
          forget to put a second copy of your shipping label in your bag, too.
        </p>
      </>
    ),
  },
  {
    id: 'what-if-i-dont-have-a-printer',
    title: "What if I don't have a printer?",
    content: (
      <>
        <p>
          You can email the shipping label to your local FedEx, UPS, or DHL for printing. Or, if
          time allows, for an additional fee we can mail printed labels directly to you - just give
          us a heads-up when placing your order.
        </p>
      </>
    ),
  },
  {
    id: 'how-do-i-track-my-shipment',
    title: 'How do I track my shipment?',
    content: (
      <>
        <p>
          We&apos;ll keep you posted with email updates and optional text alerts when your shipment
          is picked up, out for delivery, and delivered. You&apos;ll also find a tracking link in
          your confirmation email. To track through your account, simply:
        </p>
        <ol className="list-inside list-decimal p-2 [&_li]:type-body-8">
          <li>
            Visit <a href="https://www.ShipSticks.com">www.ShipSticks.com</a>
          </li>
          <li>Log in (top-right corner)</li>
          <li>
            Go to <strong>My Account</strong> &gt; <strong>Shipments</strong>
          </li>
          <li>Select the shipment you&apos;d like to track</li>
        </ol>
      </>
    ),
  },
  {
    id: 'is-a-signature-required-upon-delivery',
    title: 'Is a signature required upon delivery?',
    content: (
      <>
        <p>
          A signature is not required, but it&apos;s ultimately up to the driver&apos;s discretion.
          If the driver considers the delivery area secure or your destination has a
          shipping/receiving area, your shipment will be delivered without a signature. If this is a
          concern, we recommend shipping to a supervised location, like a business, golf course,
          resort, friend or family member, or carrier store (fees may apply).
        </p>
      </>
    ),
  },
  {
    id: 'can-i-have-my-bag-returned-to-a-different-address',
    title:
      'Can I have my bag returned to a different address than the one it was \
      originally shipped from?',
    content: (
      <>
        <p>
          That&apos;s easy! Just book two separate one-way shipments - one to send your bag out, and
          another to have it returned to a different address. Don&apos;t worry, the cost is the same
          as a round-trip shipment. This also works if your trip has multiple stops and you need
          your bag sent to different places along the way.
        </p>
      </>
    ),
  },
  {
    id: 'do-i-have-to-pack-my-bag-differently-if-i-am-shipping-internationally',
    title: 'Do I have to pack my bag differently if I am shipping internationally?',
    content: (
      <>
        <p>
          For international shipments, <strong>no locks are allowed</strong>, including TSA locks,
          since customs may need to inspect your package.
        </p>
        <p>
          Here&apos;s what you can include in your <strong>golf bag</strong>: clubs, bags, balls,
          golf shoes, and rain gear like umbrellas or jackets and pants. For{' '}
          <strong>luggage</strong>, you can pack worn clothing, new clothing with price tags
          removed, footwear, headwear, and undergarments.
        </p>
        <p>
          Some things are <strong>off-limits</strong>, like toiletries (soap, shampoo, toothpaste),
          medications, pressurized containers (aerosol cans or CO2 cartridges), cash or negotiable
          items, electronics like rangefinders and GPS devices, batteries, alcohol or anything
          containing alcohol (including perfume and cologne), tobacco, firearms and ammunition, and
          food, vitamins, or other consumables.
        </p>
        <p>
          Before shipping, please <strong>remove all price tags</strong>. For the full list of
          prohibited items, check out our{' '}
          <a href="https://www.shipsticks.com/terms">Terms of Use</a>.
        </p>
      </>
    ),
  },
  {
    id: 'can-i-make-changes-to-my-order',
    title: 'Can I make changes to my order?',
    content: (
      <>
        <p>
          Yes! You can update your order using the &quot;Edit Shipment&quot; link in your
          confirmation email or by logging into your account. Just make sure to do it before your
          shipment is in transit. Once it&apos;s on the move, you&apos;ll need to contact customer
          support to request changes, and fees may apply.
        </p>
      </>
    ),
  },
]

const shipPageHelpCenterItems = [
  {
    id: 'what-size-should-i-choose',
    title: 'What size should I choose?',
    content: (
      <>
        <p>
          Use the maximum dimension (length + width + height) and total weight of your shipment to
          determine the correct bag size.
        </p>

        <table className="mt-4 w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="border px-3 py-2">Bag Type</th>
              <th className="border px-3 py-2">Maximum Dimension</th>
              <th className="border px-3 py-2">Maximum Weight</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-3 py-2">Standard Golf Bag</td>
              <td className="border px-3 py-2">72 inches</td>
              <td className="border px-3 py-2">42 lbs</td>
            </tr>
            <tr>
              <td className="border px-3 py-2">XL Golf Bag</td>
              <td className="border px-3 py-2">79 inches</td>
              <td className="border px-3 py-2">56 lbs</td>
            </tr>
            <tr>
              <td className="border px-3 py-2">Carry-On Luggage</td>
              <td className="border px-3 py-2">45 inches</td>
              <td className="border px-3 py-2">25 lbs</td>
            </tr>
            <tr>
              <td className="border px-3 py-2">Checked Luggage</td>
              <td className="border px-3 py-2">62 inches</td>
              <td className="border px-3 py-2">50 lbs</td>
            </tr>
            <tr>
              <td className="border px-3 py-2">Oversized Luggage</td>
              <td className="border px-3 py-2">75 inches</td>
              <td className="border px-3 py-2">65 lbs</td>
            </tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'pickup',
    title: 'What does pickup date mean?',
    content: (
      <>
        <p>
          The pickup date is when our carrier partner will come by your address to collect your
          shipment, sometime between 9:00 AM and 6:00 PM local time.
        </p>
      </>
    ),
  },
  {
    id: 'dropoff',
    title: 'What does dropoff date mean?',
    content: (
      <>
        <p>
          The drop-off date is the day you&apos;ll need to send your shipment. Just take it to a
          local carrier store that matches the carrier on your shipping label (FedEx, UPS, or DHL),
          and be sure to drop it off before their cutoff time, which can vary by location and
          service level.
        </p>
      </>
    ),
  },
  {
    id: 'does-shipsticks-deliver-on-weekends',
    title: 'Does ShipSticks deliver on weekends?',
    content: (
      <>
        <p>
          Deliveries can only be scheduled Monday through Friday to ensure your shipment is handled
          during regular business days when all of our partnered carriers are operating. That said,
          depending on the route, your gear might still arrive on a weekend.
        </p>
      </>
    ),
  },
  {
    id: 'how-will-i-know-where-to-pick-up-my-bag-when-i-arrive-at-my-destination',
    title: 'How will I know where to pick up my bag when I arrive at my destination?',
    content: (
      <>
        <p>
          In most cases, golf courses will have your bag ready for pickup at the golf shop or bag
          room. Since policies can vary by location, we recommend checking with your destination
          beforehand to be certain.
        </p>
      </>
    ),
  },
  {
    id: 'do-i-need-to-confirm-that-the-golf-course-resort-or-hotel-will-accept-my-shipment',
    title: 'Do I need to confirm that the golf course, resort, or hotel will accept my shipment?',
    content: (
      <>
        <p>
          Yes. Before placing your order, we recommend reaching out to your destination to let them
          know about your shipment and your expected arrival date.
        </p>
      </>
    ),
  },
  {
    id: 'do-hotels-and-golf-courses-charge-to-receivehold-my-gear',
    title: 'Do hotels and golf courses charge to receive/hold my gear?',
    content: (
      <>
        <p>
          Policies vary by location. Some properties may charge a fee for receiving or holding
          shipments, so it&apos;s best to check directly with them beforehand.
        </p>
      </>
    ),
  },
  {
    id: 'what-is-considered-a-business-day',
    title: 'What is considered a business day?',
    content: (
      <>
        <p>Business days are Monday through Friday, excluding major holidays.</p>
      </>
    ),
  },
  {
    id: 'is-the-day-my-bag-is-picked-up-or-dropped-off-considered-one-of-the-business-days',
    title: 'Is the day my bag is picked up (or dropped off) considered one of the business days?',
    content: (
      <>
        <p>
          No, the pickup or drop-off day is not counted as a business day. Transit time starts the
          next business day.
        </p>
      </>
    ),
  },
  {
    id: 'what-happens-if-my-bag-is-overweight',
    title: 'What happens if my bag is overweight?',
    content: (
      <>
        <p>
          If your bag weighs more than the limit for your chosen size, there could be extra fees and
          possible delays. To keep things running smoothly, we recommend sizing up if you&apos;re
          unsure - better safe than sorry!
        </p>
      </>
    ),
  },
  {
    id: 'what-if-i-cant-weigh-my-bag',
    title: "What if I can't weigh my bag?",
    content: (
      <>
        <p>
          Give your best estimate when booking, or head to a local FedEx, UPS, or DHL to have your
          bag weighed. If the weight is significantly heavier than expected, additional fees may
          apply.
        </p>
      </>
    ),
  },
  {
    id: 'do-i-have-to-be-home-when-my-bags-are-picked-up',
    title: 'Do I have to be home when my bags are picked up?',
    content: (
      <>
        <p>
          You don&apos;t need to be home for pickup. Just leave your securely packed and labeled
          shipment outside your front door during the pickup window, at your discretion. If you
          don&apos;t want to leave it unattended and can&apos;t be home, you can drop it off at a
          local FedEx, UPS, or DHL store - just make sure it matches the carrier listed on your
          shipping label.
        </p>
      </>
    ),
  },
  {
    id: 'can-i-choose-my-carrier-drop-off-location',
    title: 'Can I choose my carrier drop-off location?',
    content: (
      <>
        <p>
          While we assign your carrier (FedEx, UPS, or DHL) when you book, you&apos;re free to drop
          off your shipment at any local location serviced by that carrier.
        </p>
      </>
    ),
  },
  {
    id: 'how-many-days-in-advance-should-i-schedule-my-gear-to-arrive',
    title: 'How many days in advance should I schedule my gear to arrive?',
    content: (
      <>
        <p>
          We suggest scheduling your gear to arrive at least one business day before your first
          round. For example, if your tee time is on Monday, plan to have your clubs delivered by
          the Friday before.
        </p>
      </>
    ),
  },
]

const packageAndProtectionHelpCenterItems = [
  {
    id: 'package-help-1',
    title: 'Does ShipSticks provide packaging?',
    content: (
      <>
        <p>
          We don&apos;t supply packaging, but no worries - you can simply use your own hard case,
          soft travel bag, or box to ship your gear.
        </p>
      </>
    ),
  },
  {
    id: 'do-i-need-a-box',
    title: 'Do I need a box?',
    content: (
      <>
        <p>
          Nope, no box needed! If you&apos;re shipping a golf bag in an airline-approved soft travel
          bag or hard case, you&apos;re good to go. Same goes for luggage - as long as it&apos;s
          packed in a suitcase or duffle bag.
        </p>
      </>
    ),
  },
  {
    id: 'where-do-i-get-a-box',
    title: 'Where do I get a box?',
    content: (
      <>
        <p>
          If you have a soft travel bag or hard case, you don&apos;t need a box - that&apos;s the
          beauty of our service. However, if you need a box, you can usually buy one at your local
          UPS or FedEx store for a small fee (prices vary by location). We also sell golf bag-sized
          boxes - just reach out to Customer Support to order. Here&apos;s our pricing and shipping
          times for boxes:
        </p>
        <p>
          <strong>Ground Shipping (5–7 Business Days)</strong>
        </p>
        <ul>
          <li>1 Box: $29.99</li>
          <li>2 Boxes: $34.99</li>
        </ul>
        <p>
          <strong>Express Shipping (1–3 Business Days)</strong>
        </p>
        <ul>
          <li>1 Box: $39.99</li>
          <li>2 Boxes: $44.99</li>
        </ul>
      </>
    ),
  },
  {
    id: 'insurance',
    title: 'What insurance is included?',
    content: (
      <>
        <p>
          Domestic shipments come with $1,000 of insurance for golf bags and $500 for luggage.
          International shipments include $500 for either. Need more coverage? You can add extra for
          a small fee. We recommend choosing an amount that reflects the full value of your gear and
          the bag it&apos;s packed in.
        </p>
      </>
    ),
  },
]

const siteChrome: SiteChromeContent = {
  brandName: 'ShipSticks',
  logoUrl: {
    dark: '/brands/shipsticks_dark.svg',
    light: '/brands/shipsticks_light.svg',
  },
  concierge: {
    display: '(561) 429-3900',
    href: 'tel:+15614293900',
  },
  conciergeTeam: {
    heading: (
      <>
        We&rsquo;re Here <BrandEmphasis>For You</BrandEmphasis>
      </>
    ),
    body: (
      <>
        If you&rsquo;d like help arranging your shipment, your itinerary changes, or a question
        arises, our concierge team is ready to assist. Connect by phone, text, email, or chat —
        whichever you prefer.
      </>
    ),
    cardTitle: 'Concierge Team',
    phone: { display: '(561) 429-3900', href: 'tel:+15614293900' },
    email: { display: 'info@shipsticks.com', href: 'mailto:info@shipsticks.com' },
    hours: [
      { label: 'Mon - Fri', value: '8:00a - 8:00p EST' },
      { label: 'Sat', value: '9:00a - 5:00p EST' },
      { label: 'Sun', value: '9:00a - 6:00p EST' },
    ],
  },
  footer: {
    email: { display: 'info@shipsticks.com', href: 'mailto:info@shipsticks.com' },
    phone: { display: '(561) 429-3900', href: 'tel:+15614293900' },
    address: ['11400 N. Jog Road, Suite 300', 'Palm Beach Gardens, Florida 33418'],
  },
  hero: {
    headline: (
      <>
        Pack Everything.
        <br />
        <BrandEmphasis variant="bright">Carry Nothing.</BrandEmphasis>
      </>
    ),
    subheadline: 'Let us ship your clubs so you can travel freely.',
    imageUrl: '/images/shipsticks/hero-background.jpg',
  },
  brandFamily: {
    cards: [
      {
        brandKey: 'shipsticks',
        href: 'https://shipsticks.com',
        ctaCopy: 'Visit ShipSticks',
        logoUrl: '/brands/shipsticks_graphic_dark.svg',
        brandName: 'ShipSticks',
        description:
          'Trusted by over 1 million golfers.Ships golf clubs and sports equipment' +
          ' directly to courses, resorts, and hotels worldwide.',
      },
      {
        brandKey: 'shipskis',
        href: 'https://shipskis.com',
        ctaCopy: 'Visit ShipSkis',
        logoUrl: '/brands/shipskis_graphic_dark.svg',
        brandName: 'ShipSkis',
        description: 'Ships skis, snowboards, and winter sports equipment to resorts globally.',
      },
      {
        brandKey: 'shipcamps',
        href: 'https://shipcamps.com',
        ctaCopy: 'Visit ShipCamps',
        logoUrl: '/brands/shipcamps_graphic_dark.svg',
        brandName: 'ShipCamps',
        description:
          'Ships trunks, duffels, and gear to and from summer camps. Parents skip' +
          ' the car packing chaos — gear arrives before their kids do.',
      },
    ],
  },
}

const content: Content = {
  defaultHelpCenterItems,
  shipPageHelpCenterItems,
  packageAndProtectionHelpCenterItems,
  signedInUserLinks,
  siteFooterMeta,
  siteChrome,
}

export default content
